/**
 * Firebase実装のマップリポジトリ
 */

import { v4 as uuidv4 } from 'uuid';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { IMapRepository, MapModel } from '@/repositories/interfaces';
import { getFirestoreInstance } from '@/lib/firebase';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';

/**
 * Firebase実装のマップリポジトリクラス
 */
export class FirebaseMapRepository implements IMapRepository {
  private db = getFirestoreInstance();

  /**
   * 指定IDのマップを取得
   */
  public async getMap(id: string): Promise<MapModel | null> {
    try {
      const mapDoc = await getDoc(doc(this.db, 'maps', id));

      if (mapDoc.exists()) {
        const data = mapDoc.data();
        return {
          id: mapDoc.id,
          name: data.name,
          nodes: data.nodes || [],
          edges: data.edges || [],
          transcript: data.transcript || '',
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        };
      }

      return null;
    } catch (error) {
      console.error('Error getting map:', error);
      throw error;
    }
  }

  /**
   * ユーザーのプロジェクトを取得
   */
  public async getUserProject(userId: string, projectId: string): Promise<MapModel | null> {
    try {
      const projectDoc = await getDoc(doc(this.db, `users/${userId}/projects`, projectId));

      if (projectDoc.exists()) {
        const data = projectDoc.data();
        
        // ノードとエッジを取得
        const nodesSnapshot = await getDocs(collection(this.db, `users/${userId}/projects/${projectId}/nodes`));
        const edgesSnapshot = await getDocs(collection(this.db, `users/${userId}/projects/${projectId}/edges`));
        
        // ノードを適切に型変換
        const nodes = nodesSnapshot.docs.map(doc => {
          const nodeData = doc.data();
          return {
            id: doc.id,
            title: nodeData.title || '',
            phaseId: nodeData.phaseId || '',
            defaultImportance: nodeData.defaultImportance || 3,
            extractionPrompt: nodeData.extractionPrompt || '',
            contents: nodeData.contents || [],
            content: nodeData.content || '',
            importance: nodeData.importance || 3,
            status: nodeData.status || 'pending',
            error: nodeData.error || null,
            active: nodeData.active || false,
            position: nodeData.position || { x: 0, y: 0 },
            width: nodeData.width,
            height: nodeData.height
          } as TopicNode;
        });
        
        // エッジを適切に型変換
        const edges = edgesSnapshot.docs.map(doc => {
          const edgeData = doc.data();
          return {
            id: doc.id,
            source: edgeData.source || '',
            target: edgeData.target || '',
            question: edgeData.question || '',
            strength: edgeData.strength || 'medium',
            extractionPrompt: edgeData.extractionPrompt || '',
            contents: edgeData.contents || [],
            content: edgeData.content || '',
            status: edgeData.status || 'pending',
            error: edgeData.error || null,
            active: edgeData.active || false
          } as TopicEdge;
        });
        
        return {
          id: projectDoc.id,
          name: data.title || 'Untitled',
          nodes: nodes,
          edges: edges,
          transcript: data.transcript || '',
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        };
      }

      return null;
    } catch (error) {
      console.error('Error getting user project:', error);
      throw error;
    }
  }
  
  /**
   * 全マップを取得
   */
  public async getAllMaps(): Promise<MapModel[]> {
    try {
      const mapsQuery = query(
        collection(this.db, 'maps'),
        orderBy('updatedAt', 'desc')
      );

      const mapsSnapshot = await getDocs(mapsQuery);
      const maps: MapModel[] = [];

      mapsSnapshot.forEach(mapDoc => {
        const data = mapDoc.data();
        maps.push({
          id: mapDoc.id,
          name: data.name,
          nodes: data.nodes || [],
          edges: data.edges || [],
          transcript: data.transcript || '',
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      });

      return maps;
    } catch (error) {
      console.error('Error getting all maps:', error);
      throw error;
    }
  }

  /**
   * 特定ユーザーのプロジェクト一覧を取得
   */
  public async getUserProjects(userId: string): Promise<MapModel[]> {
    try {
      const projectsQuery = query(
        collection(this.db, `users/${userId}/projects`),
        orderBy('updatedAt', 'desc')
      );

      const projectsSnapshot = await getDocs(projectsQuery);
      const projects: MapModel[] = [];

      for (const projectDoc of projectsSnapshot.docs) {
        const data = projectDoc.data();
        
        // 基本情報だけを返し、ノードとエッジは含めない（パフォーマンス向上のため）
        projects.push({
          id: projectDoc.id,
          name: data.title || 'Untitled',
          nodes: [], // 一覧表示時は空の配列
          edges: [], // 一覧表示時は空の配列
          transcript: data.transcript || '',
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      }

      return projects;
    } catch (error) {
      console.error('Error getting user projects:', error);
      throw error;
    }
  }
  
  /**
   * 新しいマップを作成
   */
  public async createMap(map: Omit<MapModel, 'id' | 'createdAt' | 'updatedAt'>): Promise<MapModel> {
    try {
      const id = uuidv4();
      const now = Timestamp.now();

      const mapData = {
        name: map.name,
        nodes: map.nodes,
        edges: map.edges,
        transcript: map.transcript,
        createdAt: now,
        updatedAt: now
      };

      await setDoc(doc(this.db, 'maps', id), mapData);

      return {
        ...map,
        id,
        createdAt: now.toDate(),
        updatedAt: now.toDate()
      };
    } catch (error) {
      console.error('Error creating map:', error);
      throw error;
    }
  }

  /**
   * 既存マップを更新
   */
  public async updateMap(
    id: string,
    update: Partial<Omit<MapModel, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<MapModel | null> {
    try {
      const mapDoc = await getDoc(doc(this.db, 'maps', id));

      if (!mapDoc.exists()) {
        return null;
      }

      const data = mapDoc.data();
      const now = Timestamp.now();

      const updateData: any = {
        updatedAt: now
      };

      if (update.name !== undefined) updateData.name = update.name;
      if (update.nodes !== undefined) updateData.nodes = update.nodes;
      if (update.edges !== undefined) updateData.edges = update.edges;
      if (update.transcript !== undefined) updateData.transcript = update.transcript;

      await updateDoc(doc(this.db, 'maps', id), updateData);

      return {
        id,
        name: update.name !== undefined ? update.name : data.name,
        nodes: update.nodes !== undefined ? update.nodes : data.nodes,
        edges: update.edges !== undefined ? update.edges : data.edges,
        transcript: update.transcript !== undefined ? update.transcript : data.transcript,
        createdAt: data.createdAt.toDate(),
        updatedAt: now.toDate()
      };
    } catch (error) {
      console.error('Error updating map:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトの更新
   */
  public async updateUserProject(
    userId: string,
    projectId: string,
    update: Partial<Omit<MapModel, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<MapModel | null> {
    try {
      const projectDoc = await getDoc(doc(this.db, `users/${userId}/projects`, projectId));

      if (!projectDoc.exists()) {
        return null;
      }

      const data = projectDoc.data();
      const now = Timestamp.now();

      const updateData: any = {
        updatedAt: now
      };

      if (update.name !== undefined) updateData.title = update.name;
      if (update.transcript !== undefined) updateData.transcript = update.transcript;

      await updateDoc(doc(this.db, `users/${userId}/projects`, projectId), updateData);

      // 更新したプロジェクトを返す
      return await this.getUserProject(userId, projectId);
    } catch (error) {
      console.error('Error updating user project:', error);
      throw error;
    }
  }

  /**
   * マップを削除
   */
  public async deleteMap(id: string): Promise<boolean> {
    try {
      await deleteDoc(doc(this.db, 'maps', id));
      return true;
    } catch (error) {
      console.error('Error deleting map:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトを削除
   */
  public async deleteUserProject(userId: string, projectId: string): Promise<boolean> {
    try {
      // サブコレクション（ノード、エッジなど）の削除は Cloud Functions で行うことが推奨されますが、
      // ここでは簡略化のため、直接ノードとエッジも削除します
      const nodesSnapshot = await getDocs(collection(this.db, `users/${userId}/projects/${projectId}/nodes`));
      const edgesSnapshot = await getDocs(collection(this.db, `users/${userId}/projects/${projectId}/edges`));

      // バッチ処理で実装するとより効率的ですが、ここでは簡略化のため逐次処理
      for (const nodeDoc of nodesSnapshot.docs) {
        await deleteDoc(doc(this.db, `users/${userId}/projects/${projectId}/nodes`, nodeDoc.id));
      }

      for (const edgeDoc of edgesSnapshot.docs) {
        await deleteDoc(doc(this.db, `users/${userId}/projects/${projectId}/edges`, edgeDoc.id));
      }

      // プロジェクト自体を削除
      await deleteDoc(doc(this.db, `users/${userId}/projects`, projectId));
      
      return true;
    } catch (error) {
      console.error('Error deleting user project:', error);
      throw error;
    }
  }
}